import { Injectable, Signal, signal, WritableSignal } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { associateWith } from '@ps-gui/shared-utils';

export interface NavigationActionState {
  displayName: string;
  isActive: boolean;
  inactiveReason?: string;
}

const navigationActionKeys = ['continue', 'back', 'skip'] as const;
export type NavigationAction = (typeof navigationActionKeys)[number];

const initialNavigationActionStates: Record<NavigationAction, NavigationActionState> = {
  back: {
    displayName: 'Zurück',
    isActive: false,
  },
  continue: {
    displayName: 'Weiter',
    isActive: true,
  },
  skip: {
    displayName: 'Dokument überspringen',
    isActive: true,
  },
};

@Injectable({
  providedIn: 'root',
})
export class PageNavigationService {
  private readonly _navigationStates: Record<
    NavigationAction,
    WritableSignal<NavigationActionState>
  > = associateWith(navigationActionKeys, (action) =>
    signal<NavigationActionState>(initialNavigationActionStates[action]),
  );

  navigationState(action: NavigationAction): Signal<NavigationActionState> {
    return this._navigationStates[action].asReadonly();
  }

  private readonly navigationSubjects = associateWith(
    navigationActionKeys,
    () => new Subject<void>(),
  );

  navigate$(action: NavigationAction): Observable<void> {
    return this.navigationSubjects[action].asObservable();
  }

  enableNavigation(action: NavigationAction): void {
    this._navigationStates[action].update((oldState) => ({
      ...oldState,
      isActive: true,
      inactiveReason: undefined,
    }));
  }

  disableNavigation(action: NavigationAction, reason?: string): void {
    this._navigationStates[action].update((value) => ({
      ...value,
      inactiveReason: reason,
      isActive: false,
    }));
  }

  triggerNavigation(action: NavigationAction) {
    this.navigationSubjects[action].next();
  }
}
